'use client';

import { useRouter } from 'next/navigation';
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Modal } from '@/app/components/Modal';
import { CLIENT_ID_QUERY_KEY } from '@/consts/const';
import { getAirRequestPath } from '@/consts/paths-utils';
import { useSearchQuery } from '@/hooks/useSearchQuery';
import { useCreateAirRequestMutation } from '../../../../hooks/useCreateAirRequestMutation';
import { SelectDraftModal } from './SelectDraftModal';
import FileIcon from '/public/icons/file-2.svg';
import LoaderSmall from '/public/icons/loader-small.svg';
import PencilIcon from '/public/icons/pencil-2.svg';

interface RequestOptionButtonProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
  disabled?: boolean;
  isLoading?: boolean;
}

const RequestOptionButton: React.FC<RequestOptionButtonProps> = ({
  icon,
  title,
  description,
  onClick,
  disabled = false,
  isLoading = false,
}) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex flex-col items-center gap-4 w-full md:w-1/2 px-4 py-5 shadow-card border border-grey-400 rounded-[8px] cursor-pointer hover:bg-blue-50 hover:border-blue-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:border-grey-400"
    >
      {isLoading ? <LoaderSmall className="w-5 h-5 text-white animate-spin" /> : icon}

      <div className="flex flex-col items-center gap-[2px]">
        <h5 className="text-black">{title}</h5>
        <p className="md:text-body-regular text-body-small text-grey-700 text-center">{description}</p>
      </div>
    </button>
  );
};

interface AirRequestInitiationModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId?: number;
}

export const AirRequestInitiationModal: React.FC<AirRequestInitiationModalProps> = ({
  isOpen,
  onClose,
  clientId: clientIdParam,
}) => {
  const router = useRouter();
  const [searchParams] = useSearchQuery();
  const clientId = clientIdParam ?? searchParams.get(CLIENT_ID_QUERY_KEY);
  const { mutateAsync: createAirRequest, isPending } = useCreateAirRequestMutation({ clientIdParam });
  const [isSelectDraftModalOpen, setIsSelectDraftModalOpen] = useState(false);

  const handleStartFromScratch = async () => {
    try {
      if (clientId) {
        const response = await createAirRequest(undefined);
        if (response.data.id) {
          router.push(getAirRequestPath(String(response.data.id), String(clientId)));
        }
      }
    } catch (error) {
      console.error(error);
      toast.error('Could not create air request. Please try again.');
    }
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <>
        {isSelectDraftModalOpen ? (
          <SelectDraftModal onGoBack={() => setIsSelectDraftModalOpen(false)} clientId={clientIdParam} />
        ) : (
          <div className="py-6 md:py-[46px] px-4 md:px-[36px] flex flex-col items-center gap-6 md:gap-[30px] w-full md:w-[592px]">
            <h2 className="light text-blue-900 text-center max-w-[255px]">
              How do you want to start your air request?
            </h2>
            <div className="flex max-md:flex-col gap-4 md:gap-5 w-full">
              <RequestOptionButton
                icon={<PencilIcon className="w-8 h-8" />}
                title="Start from scratch"
                description="Begin a new air request form"
                onClick={handleStartFromScratch}
                disabled={isPending}
                isLoading={isPending}
              />
              <RequestOptionButton
                icon={<FileIcon className="w-8 h-8 text-blue-300 stroke-blue-300" />}
                title="Choose from drafts"
                description="Continue from a saved draft."
                onClick={() => setIsSelectDraftModalOpen(true)}
                disabled={isPending}
              />
            </div>
          </div>
        )}
      </>
    </Modal>
  );
};
